/*
 * Copyright (c) Fing Ltd. All rights reserved.
 */

import React, {Component, useEffect, useState} from 'react';

import {Link, Redirect, withRouter} from "react-router-dom";

import AppNavbar from "./../components/AppNavbar";
import AppFooter from "../components/AppFooter";
import {isUserSignedIn, UserContext} from "../UserContext";
import WaitingComponent from "../components/WaitingComponent";

import {
    planPrettyName, FING_SUBSTATUS_ACTIVE, FING_SUBSTATUS_TRIALING
} from "../helpers/BillingHelper";
import {isEmptyObject} from "../helpers/JsHelper";
import AccountConnectionSummary from "./AccountConnectionSummary";
import Welcome from "../assets/svg/stories/premium-welcome.svg"
import Failure from "../assets/svg/stories/premium-failure.svg"
import Confetti from 'react-confetti';
import AccountConnectionColumnSummary from "./AccountConnectionColumnSummary";

const ResizableConfetti = () => {
    const {height, width} = useWindowDimensions();

    return (
        <Confetti
            width={width}
            height={height}
            opacity={0.5}
        />
    );
}

function getWindowDimensions() {
    return {
        width: window.innerWidth,
        height: window.innerHeight
    };
}

function useWindowDimensions() {
    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

    useEffect(() => {
        function handleResize() {
            setWindowDimensions(getWindowDimensions());
        }

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return windowDimensions;
}

class SubscribeComplete extends Component {

    static contextType = UserContext;

    constructor(props) {
        super(props);
        this.state = {
            sessionStatus: null,
            paymentStatus: null,
            serverError: false
        };
    }

    componentDidMount() {
        const query = new URLSearchParams(this.props.location.search);
        console.log(query);
        if (query.has("session_id")) {
            let sessionId = query.get('session_id');
            fetch(`/rest/subscription/verify?session_id=${sessionId}`)
                .then(response => this.validateRestResponse(response))
                .then(response => response.json())
                .then(result => {
                    this.setState({session: result});
                })
                .catch(e => this.catchRestError(e));
        }

        window.scrollTo(0, 0)
        document.title = "Fing - Payment complete";
    }

    validateRestResponse(response) {
        if (response.status === 404) {
            this.setState({sessionStatus: "not_found", paymentStatus: "not_found"});
            throw Error(response.statusText);
        } else if (response.status !== 200) {
            this.setState({sessionStatus: "api_failed", paymentStatus: "api_failed", serverError: true});
            throw Error(response.statusText);
        }
        return response;
    }

    catchRestError(response) {
        console.log("Failed to fetch subscription: " + response);
    }

    // --------------------------------------------------------------------------------

    render() {
        return (
            <div>
                <AppNavbar mode='account' fillBackground="true"/>
                <main id="content" role="main">
                    {this.renderMainContent()}
                </main>
                <AppFooter/>
            </div>
        );
    }

    renderMainContent() {
        return (
            <UserContext.Consumer>
                {userContext => this.renderMainContentInContext(userContext)}
            </UserContext.Consumer>
        )
    }

    renderMainContentInContext(userContext) {
        const {session} = this.state;
        if (userContext.userProfileLoaded === false) {
            return '';
        }
        if (!isUserSignedIn(userContext)) {
            return (<Redirect to='/account/subscribe/cart'/>);
        }
        if (!session) {
            return <WaitingComponent className="my-3"/>
        }

        // Session statuses are: complete, expired, open
        if (session.status === 'expired') {
            return <Redirect to={"/account/subscribe/cart"} />;
        }
        if (session.status !== 'complete') {
            return <Redirect to={`/account/subscribe/embedded-checkout?session_id=${session.sessionId}`}/>;
        }

        // Payment statuses are: no_payment_required, unpaid, paid
        if (session.paymentStatus === "unpaid") {
            return this.renderUnpaid(userContext.userProfile);
        }

        return this.renderThankYou(userContext.userProfile);
    }

    renderUnpaid(userProfile) {
        const name = userProfile.firstName ? userProfile.firstName : userProfile.name;
        return <div className="container space-2">
            <div className="row justify-content-md-between align-items-md-center mb-4">
                <div className="col-md-7 col-xl-6">
                    <div className="mb-6">
                        <h1 className="mb-3">{name}, we're almost there.</h1>
                        <p className="lead">
                            Your subscription has been accepted, <span
                            className="text-highlight-warning">but the payment could not be collected yet</span>.
                        </p>
                        <p>
                            If you used a payment method that requires some time to issue a payment, this could be
                            normal. You will receive an email when the subscription becomes active. <br/>
                            If not, please verify in your bank account the reason for rejecting the transaction.
                            You can try entering a different payment method in the dashboard.
                        </p>
                    </div>
                    <div className="mb-2">
                        <Link className="btn btn-primary btn-sm my-2"
                              to="/account/dashboard">View the account dashboard</Link>
                    </div>
                </div>
                <div className="col-md-5 col-xl-6">
                    <figure className="text-center">
                        <img className="img-fluid img-fluid" src={Failure} alt={"Failed"}/>
                    </figure>
                </div>
            </div>
        </div>
    }

    renderThankYou(userProfile) {
        const name = userProfile.firstName ? userProfile.firstName : userProfile.name;
        return <div>
            <ResizableConfetti/>
            <div className="container space-2">
                <div className="row justify-content-md-between align-items-md-center mb-4">
                    <div className="col-md-7 col-xl-6">
                        <div className="mb-6">
                            <h1 className="mb-3">Thank you, {name}.</h1>
                            <p className="lead">
                                Your subscription is now active.<br/>
                                Check its status and review every invoice in the Account dashboard.</p>
                        </div>
                        <div className="mb-2">
                            <Link className="btn btn-primary btn-sm transition-3d-hover my-2"
                                  to="/account/dashboard">View the account dashboard</Link>
                        </div>
                    </div>
                    <div className="col-md-5 col-xl-6">
                        <figure className="text-center">
                            <img className="img-fluid img-fluid" src={Welcome} alt={"Welcome"}/>
                        </figure>
                    </div>
                </div>

            </div>

            {this.renderWhatIsIncluded(userProfile)}
        </div>;
    }

    renderWhatIsIncluded(userProfile) {
        return (
            <div className="bg-light">
                <div className="container space-2 border-bottom">

                    <div className="w-lg-80 text-center mx-lg-auto mb-7">
                        <span className="d-block text-secondary font-size-1 font-weight-medium text-uppercase">In your account</span>
                        <h2 className="h1 font-size-md-down-3 font-weight-semi-bold">Get the most out of your
                            subscription</h2>
                        <p>Here's a short summary of what you are already using and how to expand your network
                            awareness.</p>
                    </div>

                    <AccountConnectionColumnSummary userProfile={userProfile}/>
                </div>
            </div>
        );
    }

}

export default withRouter(SubscribeComplete)